import { Component, OnInit } from '@angular/core';
import { buttonInOut } from 'animations-lib';
import { EntityTypeLabelPair, IFieldMeta } from '../../interfaces/imetadata';
import { SignatureField } from '../../interfaces/signature';
import { FieldType } from '../../utils/fieldtype';
import { FieldExpressionProcessorService } from './field-expression-processor.service';
import { FieldInsertService } from './field-insert.service';
import { AdcMigrationFieldInfo, AdcMigrationFieldOptions, AdcMigrationStatus } from './models';

@Component({
  selector: 'app-adc-migration',
  templateUrl: './adc-migration.component.html',
  styleUrls: ['adc-migration.component.scss'],
  animations: [buttonInOut],
  standalone: false,
})
export class AdcMigrationComponent implements OnInit {
  protected readonly FieldMetaStatus = AdcMigrationStatus;

  fields: AdcMigrationFieldInfo[] = [];
  statusColours = {
    [AdcMigrationStatus.MIGRATABLE]: '#FFF',
    [AdcMigrationStatus.NOT_RECOGNISED]: '#F88',
    [AdcMigrationStatus.NOT_SUPPORTED]: '#AAA',
    [AdcMigrationStatus.NO_SUCH_FIELD]: '#88F',
    [AdcMigrationStatus.COMPLETED]: '#FFF',
  };
  migrationInProgress = false;

  constructor(
    private fieldInsertService: FieldInsertService,
    private fieldExpressionProcessorService: FieldExpressionProcessorService
  ) {}

  ngOnInit() {
    this.populateFields();
  }

  navigateToField(field: AdcMigrationFieldInfo): void {
    if (this.migrationInProgress || field.status === AdcMigrationStatus.COMPLETED) {
      return;
    }

    Word.run(async (context) => {
      const fields: Word.FieldCollection = context.document.body.fields.load('items');
      await context.sync();

      fields.items[field.index].select();
      await context.sync();
    });
  }

  deleteField(fieldInfo: AdcMigrationFieldInfo): void {
    Word.run(async (context) => {
      this.migrationInProgress = true;

      try {
        const fields: Word.FieldCollection = context.document.body.fields.load('items');
        await context.sync();

        const field = fields.items[fieldInfo.index];
        field.delete();
        await context.sync();

        fieldInfo.status = AdcMigrationStatus.COMPLETED;
      } finally {
        this.migrationInProgress = false;
      }
    });
  }

  migrateFields(fieldsToMigrateList: AdcMigrationFieldInfo[]): void {
    Word.run(async (context) => {
      try {
        this.migrationInProgress = true;

        const fields: Word.FieldCollection = context.document.body.fields.load('items');
        await context.sync();

        const deletedIndexes = [];
        for (const fieldInfo of fieldsToMigrateList) {
          if (fieldInfo.status !== AdcMigrationStatus.MIGRATABLE) {
            continue;
          }

          const field = fields.items[fieldInfo.index];
          field.select();
          await context.sync();

          // TODO: make this more explicit? (i.e. type)
          if ('meta' in fieldInfo.meta) {
            // data field
            const fieldMeta = fieldInfo.meta.meta;

            // TODO: better checks - what if parent -> parent it to-many?
            if (!fieldMeta || fieldMeta.parent?.type === FieldType.TO_MANY) {
              continue;
            }

            // all fields on route to selected field
            const ancestors: IFieldMeta[] = [];
            let parent = fieldMeta.parent;
            while (parent) {
              ancestors.unshift(parent);
              parent = parent.parent;
            }

            for (const ancestorFieldMeta of ancestors) {
              await this.fireFieldInserted(fieldInfo.meta.entity, ancestorFieldMeta);
            }

            await this.fireFieldInserted(fieldInfo.meta.entity, fieldMeta, fieldInfo.meta.options);
          } else {
            // signature field
            await this.fireSignatureFieldInserted(fieldInfo.meta as SignatureField);
          }

          field.delete();
          await context.sync();
          deletedIndexes.push(fieldInfo.index);

          fieldInfo.status = AdcMigrationStatus.COMPLETED;
        }

        deletedIndexes
          .sort((a, b) => b - a)
          .forEach((index) => {
            this.fields.forEach((f) => {
              if (f.index > index) {
                f.index = f.index - 1;
              }
            });
          });
      } finally {
        this.migrationInProgress = false;
      }
    });
  }

  migrateAll(): void {
    this.migrateFields(this.fields);
  }

  refresh(): void {
    this.fields = [];
    this.populateFields();
  }

  private fireFieldInserted(entity: EntityTypeLabelPair, field: IFieldMeta, options?: AdcMigrationFieldOptions): Promise<void> {
    return new Promise<void>((resolve) => {
      this.fieldInsertService.insert({ entity: entity, field: field, resolve: resolve, options });
    });
  }

  private fireSignatureFieldInserted(signatureField: SignatureField): Promise<void> {
    return new Promise<void>((resolve) => {
      this.fieldInsertService.insertSignatureField({
        fieldEvent: { providerName: 'docusign', field: signatureField, contentControlId: undefined },
        resolve: resolve
      });
    });
  }

  private populateFields(): void {
    const MERGE_FIELD_START = `MERGEFIELD`;
    const MERGE_FIELD_END = /\\[bfmv*] (.*)/;

    Word.run(async (context) => {
      const fields: Word.FieldCollection = context.document.body.fields.load('items');
      await context.sync();

      if (fields.items.length === 0) {
        this.fields = [];
        return;
      }

      fields.load(['code', 'type']);
      await context.sync();

      for (let i = 0; i < fields.items.length; i++) {
        const field = fields.items[i];

        if (field.type !== 'MergeField') {
          console.log('Skipping ' + field.type);
          continue;
        }

        const code = field.code.trim();
        const expression = code.slice(MERGE_FIELD_START.length).replace(MERGE_FIELD_END, '').trim();
        const processedField = this.fieldExpressionProcessorService.process(expression);

        this.fields.push({
          index: i, expression: expression, description: processedField.description, status: processedField.status,
          meta: processedField.meta,
        });
      }
    });
  }
}
